import { Check, Monitor, Moon, Sun } from "lucide-react";

import type { ThemeColorScheme, ThemePreference } from "@src/features/theme";
import { PRODUCT_NAME } from "@src/shared/brand";
import { HeaderText } from "@src/shared/components/general/HeaderText";
import { Text } from "@src/shared/components/general/Text";
import {
  HorizontalFlexbox,
  VerticalFlexbox,
} from "@src/shared/components/layout/Flexbox";
import { Label } from "@src/shared/components/ui/label";
import {
  RadioGroup,
  RadioGroupItem,
} from "@src/shared/components/ui/radio-group";
import { cn } from "@src/shared/lib/utils";

export interface PreferencesSettingsPageV2Props {
  effectiveColorScheme: ThemeColorScheme;
  systemColorScheme: ThemeColorScheme;
  themePreference: ThemePreference;
  onThemePreferenceChange: (preference: ThemePreference) => void;
}

const themeOptions: Array<{
  value: ThemePreference;
  label: string;
  description: string;
  icon: typeof Monitor;
}> = [
  {
    value: "light",
    label: "Light",
    description: "Always use the light appearance.",
    icon: Sun,
  },
  {
    value: "dark",
    label: "Dark",
    description: "Always use the dark appearance.",
    icon: Moon,
  },
  {
    value: "system",
    label: "System",
    description: "Match your device's appearance.",
    icon: Monitor,
  },
];

function formatColorScheme(colorScheme: ThemeColorScheme): string {
  return colorScheme === "dark" ? "Dark" : "Light";
}

export function PreferencesSettingsPageV2({
  effectiveColorScheme,
  systemColorScheme,
  themePreference,
  onThemePreferenceChange,
}: PreferencesSettingsPageV2Props) {
  return (
    <VerticalFlexbox
      className="w-full px-4 py-[18px] md-stitch:px-8 md-stitch:py-7"
      gap="none"
    >
      <HeaderText tag="h2" className="mb-1 text-xl tracking-[-0.015em]">
        General
      </HeaderText>
      <Text appearance="subdued" className="mb-8" sizing="sm">
        Appearance and app preferences.
      </Text>

      <VerticalFlexbox as="section" className="max-w-[560px]" gap="none">
        <Text as="h3" className="mb-1" fontWeight={600} sizing="sm">
          Theme
        </Text>
        <Text appearance="subdued" className="mb-3 leading-relaxed" sizing="sm">
          Choose how {PRODUCT_NAME.singular} looks. System follows your
          device&apos;s light or dark setting.
        </Text>

        <RadioGroup
          aria-label="Theme preference"
          className="grid gap-2.5 md-stitch:grid-cols-3"
          onValueChange={(value) =>
            onThemePreferenceChange(value as ThemePreference)
          }
          value={themePreference}
        >
          {themeOptions.map((option) => {
            const Icon = option.icon;
            const isSelected = option.value === themePreference;

            return (
              <Label
                className={cn(
                  "flex min-h-[116px] cursor-pointer flex-col items-start gap-2 rounded-[11px] border bg-background px-3.5 py-3 text-left transition-colors focus-within:ring-2 focus-within:ring-ring/40 hover:border-primary/50",
                  isSelected
                    ? "border-primary bg-primary/5 shadow-[0_0_0_3px_hsl(var(--primary)/0.10)]"
                    : "border-border",
                )}
                htmlFor={`theme-preference-${option.value}`}
                key={option.value}
              >
                <HorizontalFlexbox
                  alignItems="center"
                  className="w-full"
                  justifyContent="space-between"
                >
                  <span
                    className={cn(
                      "flex size-[30px] items-center justify-center rounded-[8px] text-muted-foreground",
                      isSelected
                        ? "bg-primary text-primary-foreground"
                        : "bg-surface",
                    )}
                  >
                    <Icon aria-hidden="true" className="size-4" />
                  </span>
                  {isSelected ? (
                    <Check
                      aria-hidden="true"
                      className="size-[15px] text-primary"
                      strokeWidth={2.6}
                    />
                  ) : null}
                </HorizontalFlexbox>
                <RadioGroupItem
                  className="sr-only"
                  id={`theme-preference-${option.value}`}
                  value={option.value}
                />
                <VerticalFlexbox gap="xs">
                  <Text fontWeight={600} sizing="sm">
                    {option.label}
                  </Text>
                  <Text
                    appearance="subdued"
                    className="leading-snug"
                    sizing="xs"
                  >
                    {option.description}
                  </Text>
                </VerticalFlexbox>
              </Label>
            );
          })}
        </RadioGroup>

        <HorizontalFlexbox
          alignItems="flex-start"
          className="mt-3 rounded-[10px] border border-primary/15 bg-primary/5 px-3.5 py-3"
          gap="sm"
        >
          <Text appearance="subdued" className="leading-relaxed" sizing="sm">
            Current appearance: {formatColorScheme(effectiveColorScheme)}
            {themePreference === "system"
              ? ` (system is ${formatColorScheme(systemColorScheme).toLowerCase()})`
              : null}
          </Text>
        </HorizontalFlexbox>
      </VerticalFlexbox>
    </VerticalFlexbox>
  );
}
